<template>
    <section class="recommendation-card recommendation-card-public">
        <div class="recommendation-card__header">
            <div>
                <p class="recommendation-card__eyebrow">{{ labels.publicEyebrow }}</p>
                <h3 class="recommendation-card__title">{{ labels.publicTitle }}</h3>
                <p class="recommendation-card__subtitle">{{ labels.publicSubtitle }}</p>
            </div>
            <div class="recommendation-card__header-side">
                <span class="recommendation-card__badge">{{ versionLabel }}</span>
                <div v-if="recommendations.length > 0" class="recommendation-carousel-controls" role="group" :aria-label="labels.publicSlider">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="scrollRecommendations(-1)">
                        <i class="fa-solid fa-chevron-left"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="scrollRecommendations(1)">
                        <i class="fa-solid fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>
        <div v-if="loading" class="recommendation-empty">{{ labels.loading }}</div>
        <template v-else>
            <div class="row g-3 mb-4">
                <div class="col-12 col-md-4">
                    <div class="recommendation-stat-box recommendation-stat-box-public">
                        <span>{{ labels.totalRecommendations }}</span>
                        <strong>{{ summary.total || 0 }}</strong>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="recommendation-stat-box recommendation-stat-box-public">
                        <span>{{ labels.totalContributors }}</span>
                        <strong>{{ summary.contributors || 0 }}</strong>
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <div class="recommendation-stat-box recommendation-stat-box-public">
                        <span>{{ labels.totalDetails }}</span>
                        <strong>{{ recommendations.length }}</strong>
                    </div>
                </div>
            </div>

            <div class="recommendation-breakdowns row g-3 mb-4">
                <div class="col-12 col-lg-4">
                    <div class="breakdown-box">
                        <h4>{{ labels.byType }}</h4>
                        <ul>
                            <li v-for="item in summary.by_type || []" :key="`type-${item.label}`">{{ formatType(item.label) }} <strong>{{ item.total }}</strong></li>
                        </ul>
                    </div>
                </div>
                <div class="col-12 col-lg-4">
                    <div class="breakdown-box">
                        <h4>{{ labels.byCategory }}</h4>
                        <ul>
                            <li v-for="item in summary.by_category || []" :key="`category-${item.label}`">{{ item.label }} <strong>{{ item.total }}</strong></li>
                        </ul>
                    </div>
                </div>
                <div class="col-12 col-lg-4">
                    <div class="breakdown-box">
                        <h4>{{ labels.byLevel }}</h4>
                        <ul>
                            <li v-for="item in summary.by_level || []" :key="`level-${item.label}`">{{ item.label }} <strong>{{ item.total }}</strong></li>
                        </ul>
                    </div>
                </div>
            </div>

            <div v-if="recommendations.length === 0" class="recommendation-empty">{{ labels.publicEmpty }}</div>
            <div v-else ref="recommendationList" class="recommendation-list">
                <article
                    v-for="recommendation in recommendations"
                    :key="recommendation.id"
                    class="recommendation-item recommendation-item-public"
                    @click="$emit('open-detail', recommendation)"
                >
                    <div class="recommendation-item__top">
                        <div>
                            <div class="recommendation-item__meta">
                                <span class="recommendation-chip">{{ formatType(recommendation.recommendation_type) }}</span>
                                <span v-if="recommendation.recommendation_type === 'edit_existing_trick' && hiddenChangesCount(recommendation) > 0" class="recommendation-chip recommendation-chip-muted">+{{ hiddenChangesCount(recommendation) }} {{ labels.moreChanges }}</span>
                            </div>
                            <h4 class="recommendation-item__title">{{ recommendation.proposed_name || recommendation.trick?.name || labels.untitled }}</h4>
                            <p v-if="showSummaryLine(recommendation)" class="recommendation-item__summary">{{ summaryLine(recommendation) }}</p>
                        </div>
                        <div class="recommendation-votes">
                            <button
                                type="button"
                                class="vote-button vote-button-up"
                                :class="{ 'vote-button-active': recommendation.votes?.current_user_vote === 1 }"
                                :disabled="voteBusyId === recommendation.id"
                                @click.stop="$emit('vote', { recommendationId: recommendation.id, vote: 1 })"
                            >
                                <i class="fa-solid fa-thumbs-up"></i>
                                <span>{{ recommendation.votes?.upvotes ?? 0 }}</span>
                            </button>
                            <button
                                type="button"
                                class="vote-button vote-button-down"
                                :class="{ 'vote-button-active': recommendation.votes?.current_user_vote === -1 }"
                                :disabled="voteBusyId === recommendation.id"
                                @click.stop="$emit('vote', { recommendationId: recommendation.id, vote: -1 })"
                            >
                                <i class="fa-solid fa-thumbs-down"></i>
                                <span>{{ recommendation.votes?.downvotes ?? 0 }}</span>
                            </button>
                        </div>
                    </div>
                    <div v-if="previewChanges(recommendation).length" class="recommendation-change-preview">
                        <span v-for="change in previewChanges(recommendation)" :key="`${recommendation.id}-${change.field}`" class="recommendation-change-chip">
                            {{ change.label }}: {{ compactValue(change.old_display) }} -> {{ compactValue(change.new_display) }}
                        </span>
                    </div>
                    <div class="recommendation-item__footer">
                        <span class="recommendation-item__date">{{ formatDate(recommendation.created_at) }}</span>
                        <span class="recommendation-item__cta">{{ labels.viewDetail }}</span>
                    </div>
                </article>
            </div>
        </template>
    </section>
</template>

<script>
export default {
    name: 'TrickRecommendationPublicCard',
    emits: ['open-detail', 'vote'],
    props: {
        tricklistVersion: { type: Object, default: null },
        summary: { type: Object, default: () => ({}) },
        recommendations: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' },
        voteBusyId: { type: Number, default: null },
        labels: { type: Object, required: true },
    },
    computed: {
        versionLabel() {
            if (!this.tricklistVersion) {
                return this.labels.unresolvedVersion;
            }

            return this.tricklistVersion.name
                ? `${this.labels.version}: ${this.tricklistVersion.name}`
                : `${this.labels.version}: #${this.tricklistVersion.id}`;
        },
    },
    methods: {
        formatType(type) {
            return type === 'edit_existing_trick' ? this.labels.editType : this.labels.newType;
        },
        formatDate(value) {
            if (!value) return this.labels.notSpecified;
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return this.labels.notSpecified;
            return date.toLocaleDateString();
        },
        previewChanges(recommendation) {
            return Array.isArray(recommendation?.changed_fields)
                ? recommendation.changed_fields.slice(0, 2)
                : [];
        },
        hiddenChangesCount(recommendation) {
            const total = Array.isArray(recommendation?.changed_fields) ? recommendation.changed_fields.length : 0;
            return Math.max(0, total - this.previewChanges(recommendation).length);
        },
        compactValue(value) {
            if (value === null || value === undefined || value === '') {
                return this.labels.notSpecified;
            }

            const normalized = String(value);
            return normalized.length > 18 ? `${normalized.slice(0, 18)}...` : normalized;
        },
        summaryLine(recommendation) {
            if (recommendation?.recommendation_type === 'new_trick') {
                const parts = [
                    recommendation?.proposed_category?.name,
                    recommendation?.proposed_level?.name,
                    recommendation?.proposed_value !== null && recommendation?.proposed_value !== undefined
                        ? `${this.labels.value}: ${recommendation.proposed_value}`
                        : null,
                ].filter(Boolean);

                if (parts.length > 0) {
                    return parts.join(' · ');
                }
            }

            return recommendation?.detail || this.labels.noChangesDetected;
        },
        showSummaryLine(recommendation) {
            if (Array.isArray(recommendation?.changed_fields) && recommendation.changed_fields.length > 0) {
                return false;
            }

            return !!this.summaryLine(recommendation);
        },
        scrollRecommendations(direction) {
            const container = this.$refs.recommendationList;
            if (!container) {
                return;
            }

            const amount = Math.max(240, Math.round(container.clientWidth * 0.82));
            container.scrollBy({
                left: amount * direction,
                behavior: 'smooth',
            });
        },
    },
};
</script>

<style scoped>
.recommendation-card {
    border: 1px solid #e5e7eb;
    border-radius: 18px;
    padding: 20px;
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.recommendation-card-public {
    background: linear-gradient(135deg, rgba(253, 250, 242, 0.96), rgba(255, 255, 255, 0.95));
}

.recommendation-card__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
}

.recommendation-card__header-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
}

.recommendation-carousel-controls {
    display: inline-flex;
    gap: 8px;
}

.recommendation-card__eyebrow {
    margin: 0 0 6px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #b45309;
    font-weight: 700;
}

.recommendation-card__title {
    margin: 0;
    font-size: 1.25rem;
}

.recommendation-card__subtitle {
    margin: 6px 0 0;
    color: #64748b;
}

.recommendation-card__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #fef3c7;
    color: #92400e;
}

.recommendation-card__badge-light {
    background: #fff7ed;
    color: #9a3412;
}

.recommendation-stat-box {
    border-radius: 14px;
    padding: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.recommendation-stat-box-public {
    background: #fff7ed;
    border: 1px solid #fed7aa;
}

.recommendation-empty {
    border: 1px dashed #cbd5e1;
    border-radius: 14px;
    padding: 20px;
    text-align: center;
    color: #64748b;
}

.breakdown-box {
    height: 100%;
    border: 1px solid #fde68a;
    border-radius: 14px;
    padding: 14px;
    background: rgba(255, 251, 235, 0.85);
}

.breakdown-box h4 {
    margin: 0 0 10px;
    font-size: 0.95rem;
}

.breakdown-box ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.breakdown-box li {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    color: #475569;
}

.recommendation-list {
    display: flex;
    flex-direction: row;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 6px;
    scrollbar-gutter: stable;
    scroll-snap-type: x proximity;
}

.recommendation-item {
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.9);
    min-width: 270px;
    max-width: 270px;
    cursor: pointer;
    scroll-snap-align: start;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.recommendation-item-public {
    border-color: #fed7aa;
}

.recommendation-item-public:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 28px rgba(146, 64, 14, 0.12);
}

.recommendation-item__top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.recommendation-votes {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.vote-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #fed7aa;
    border-radius: 999px;
    background: #fff7ed;
    color: #9a3412;
    padding: 6px 10px;
    font-weight: 600;
}

.vote-button:disabled {
    opacity: 0.6;
}

.vote-button-up.vote-button-active {
    background: #dcfce7;
    border-color: #86efac;
    color: #166534;
}

.vote-button-down.vote-button-active {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #991b1b;
}

.recommendation-item__meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.recommendation-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #ffedd5;
    color: #9a3412;
}

.recommendation-chip-muted {
    background: #fff7ed;
    color: #7c2d12;
}

.recommendation-item__title {
    margin: 0;
    font-size: 1rem;
}

.recommendation-item__summary {
    margin: 8px 0 0;
    color: #475569;
    font-size: 0.9rem;
    line-height: 1.35;
    min-height: 2.45em;
}

.recommendation-change-preview {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.recommendation-change-chip {
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    padding: 5px 9px;
    border-radius: 999px;
    background: #fff7ed;
    border: 1px solid #fdba74;
    color: #9a3412;
    font-size: 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.recommendation-item__footer {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #ffedd5;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
}

.recommendation-item__date {
    color: #64748b;
    font-size: 0.8rem;
}

.recommendation-item__cta {
    color: #c2410c;
    font-size: 0.82rem;
    font-weight: 700;
}

@media (max-width: 767px) {
    .recommendation-card__header,
    .recommendation-item__top {
        flex-direction: column;
    }

    .recommendation-card__header-side {
        width: 100%;
        align-items: stretch;
    }

    .recommendation-carousel-controls {
        justify-content: flex-end;
    }

    .recommendation-item {
        min-width: 82vw;
        max-width: 82vw;
    }
}
</style>